/**
 * Iter 70 — Vendor "Request to feature" card.
 *
 * Renders inside CarForm when the vendor is editing an existing car. Shows
 * one of three states:
 *   • FEATURED — the car is already live in the homepage featured slots
 *     (admin locked it in). Displays priority + fee % as a badge.
 *   • REQUEST_PENDING — vendor already submitted a request; awaiting admin.
 *     Vendor can withdraw.
 *   • CTA — vendor can submit a new request with a max fee % they're
 *     willing to pay.
 *
 * Self-hides on brand-new cars (no `car.id` yet) so vendors save first,
 * feature later.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Clock, X, Check, TrendingUp } from "lucide-react";

export default function VendorFeatureRequestCard({ car, onChanged }) {
  const [feePct, setFeePct] = useState(String(car?.featured_request?.max_fee_percent ?? "5"));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 70d — live market signal so vendors know what to bid.
  const [market, setMarket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/featured/market").then((r) => { if (!cancelled) setMarket(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [car?.id]);

  if (!car?.id) return null; // brand-new car — save first, then request

  const isFeatured = !!car.is_featured;
  const pendingReq = car.featured_request?.status === "pending" ? car.featured_request : null;

  const submit = async () => {
    const num = Number(feePct);
    if (!Number.isFinite(num) || num < 0 || num > 50) {
      toast.error("Enter a fee % between 0 and 50");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/cars/${car.id}/request-featured`, {
        max_fee_percent: num,
        note: note.trim() || null,
      });
      toast.success("Request sent — an admin will review it shortly");
      setNote("");
      onChanged?.();
    } catch (e) {
      toast.error("Could not submit", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!confirm("Withdraw your featured-listing request?")) return;
    setBusy(true);
    try {
      await api.delete(`/cars/${car.id}/request-featured`);
      toast.success("Request withdrawn");
      onChanged?.();
    } catch (e) {
      toast.error("Could not withdraw", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (isFeatured) {
    return (
      <div
        className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-5"
        data-testid="vendor-feature-status-featured"
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-amber-600" />
          <h4 className="font-semibold text-amber-900">This car is featured on the homepage</h4>
        </div>
        <p className="text-sm text-amber-900/80">
          Priority <strong>{car.featured_priority || 0}</strong> · Extra commission{" "}
          <strong>{(car.featured_fee_percent || 0).toFixed(1)}%</strong> on each booking.
        </p>
        <p className="text-xs text-amber-900/60 mt-2">
          Contact admin support to change the featured terms or remove the slot.
        </p>
      </div>
    );
  }

  if (pendingReq) {
    return (
      <div
        className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5"
        data-testid="vendor-feature-status-pending"
      >
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-5 w-5 text-amber-600" />
          <h4 className="font-semibold text-amber-900">Featured request pending admin review</h4>
        </div>
        <p className="text-sm text-amber-900/80">
          Submitted {new Date(pendingReq.requested_at).toLocaleDateString()} · offering up to{" "}
          <strong>{pendingReq.max_fee_percent}%</strong> extra commission.
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={withdraw}
          disabled={busy}
          className="mt-3 text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid="vendor-feature-withdraw"
        >
          <X className="h-3 w-3 mr-1" /> Withdraw request
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid="vendor-feature-cta">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-5 w-5 text-amber-500" />
        <h4 className="font-semibold text-neutral-900">Get featured on the homepage</h4>
      </div>
      <p className="text-sm text-neutral-600 mb-4">
        Featured cars appear in the top-of-page teaser and the mid-page showcase, drawing
        substantially more views than the standard listing grid. Offer a small extra
        commission (typically 3–10%) in exchange for the slot — admin reviews every request
        and sets the final rate.
      </p>

      {market && (market.top_bid > 0 || market.active_top > 0) && (
        <div
          className="mb-4 rounded-xl bg-neutral-50 border border-neutral-200 p-3 flex items-center gap-3 flex-wrap text-xs"
          data-testid="vendor-feature-market-strip"
        >
          <TrendingUp className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-neutral-600">Current featured slot market:</span>
          {market.top_bid > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-semibold">
              Top pending bid <strong data-testid="vendor-feature-top-bid">{market.top_bid.toFixed(1)}%</strong>
            </span>
          )}
          {market.active_top > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-900 px-2 py-0.5 font-semibold">
              Live floor {market.active_floor.toFixed(1)}% – top {market.active_top.toFixed(1)}%
            </span>
          )}
          {market.pending_count > 0 && (
            <span className="text-neutral-500">
              · {market.pending_count} vendor{market.pending_count === 1 ? "" : "s"} in the queue
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-neutral-600 mb-1">
            Max extra commission % you accept
          </label>
          <Input
            type="number"
            min={0}
            max={50}
            step={0.5}
            value={feePct}
            onChange={(e) => setFeePct(e.target.value)}
            className="h-10"
            data-testid="vendor-feature-fee-input"
          />
        </div>
        <div className="flex-[2]">
          <label className="block text-xs font-semibold text-neutral-600 mb-1">
            Note to admin (optional)
          </label>
          <Input
            type="text"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Just added new photos, ready for summer season"
            className="h-10"
            data-testid="vendor-feature-note-input"
          />
        </div>
        <Button
          onClick={submit}
          disabled={busy}
          className="h-10 rounded-full bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="vendor-feature-submit"
        >
          <Check className="h-4 w-4 mr-1" /> Request featured
        </Button>
      </div>
    </div>
  );
}
